import {api, RequestParam} from "@/shared/api";

import {DownloadStatus} from "@/shared/ui/downloadStatus";
import {showPdfPreview} from "@/features/pdfPreview/showPdfPreview";
import {confirmExportOfEmptyTable} from "@/pages/reports/confirmExportOfEmptyTable";
import {WorkingTimeDialog} from "@/features/workingtime/workingTimeDialog";
import {WorkingTimeNoticeDialogBridge} from "@/features/requests/workingTimeNoticeDialog/workingTimeNoticeDialogBridge";
import {TimeNavigationBar} from "@/shared/ui/timeNavigationBar";
import {Combobox} from "@/shared/ui/combobox";
import {GroupDropdown} from "@/shared/ui/groupDropdown";
import {WorkingTimeRequestTable} from "@/features/requests/workingTimeRequestTable";
import {WorkingTimeRequestUtil} from "@/shared/workingtime/workingTimeRequestUtil";
import * as Constants from "@/shared/constants";
import {isWorkingTimeDateTimeChangeAllowed, isWorkingTimeTypeAllowedForAddAbsence} from "@/shared/securityHelper";
import {BrowserStorageSettings} from "@/shared/browserStorageSettings";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import {DOMUtil} from "@/shared/utils/dom";
import {Analytics} from "@/shared/analytics";
import {SecurityStatus} from "@/shared/securityStatus";
import {CustomFieldDefinition, User, WorkingTimeType} from "@/shared/timr";
import {getISODate, parseISODate, Temporal} from "@/shared/utils/datefunctions";
import {DateTimeChangeableByUserDayRangeMode, ExportType} from "@/shared/timrTypes";

export interface WorkingTimeRequestsReportConfig {
	users: User[];
	workingTimeTypes: WorkingTimeType[];
	workingTimeTypesForNotices: WorkingTimeType[];
	isGroupLeader: boolean;
	currentUserId: number;
	isGroupLeaderForThemself: boolean;
	customFieldDefinitions: CustomFieldDefinition[];
	defaultStatusNumber: number;
	allowedUserIds: number[];
	workingTimeDateTimeChangeableByUserDayRangeMode: DateTimeChangeableByUserDayRangeMode;
	workingTimeDateTimeChangeableByUserDayRangeUntil: string | null;
}

export class WorkingTimeRequestsReportPage {
	private workingTimeDialog: WorkingTimeDialog;
	private workingTimeNoticeDialog: WorkingTimeNoticeDialogBridge;
	private timeNavigationBar: TimeNavigationBar;
	private userCombobox: Combobox<User>;
	private substituteCombobox: Combobox<User>;
	private groupDropdown: GroupDropdown;
	private workingTimeRequestTable: WorkingTimeRequestTable;

	constructor(public config: WorkingTimeRequestsReportConfig) {
		this.init();
	}

	private init() {
		this.initWorkingTimeDialog();
		WorkingTimeRequestUtil.toggleWorkingTimeTypesByRequestNotAvailableWarning(this.config.workingTimeTypes);
		const workingTimeTypeSelect = $("#workingTimeRequestFilterWorkingTimeTypeId");
		DOMUtil.fillWorkingTimeTypeSelect(workingTimeTypeSelect, this.config.workingTimeTypes, ["", messages["filter.all"]]);
		workingTimeTypeSelect.change(() => {
			this.filterTable();
		});

		this.timeNavigationBar = new TimeNavigationBar({
			container: $(".breadcrumb-line-dashboard"),
			timeSpanChanged: (from, to, type) => {
				BrowserStorageSettings.setReportingDateFrom(Constants.GROUP_TIME_NAV_BAR, from);
				BrowserStorageSettings.setReportingDateTo(Constants.GROUP_TIME_NAV_BAR, to);
				BrowserStorageSettings.setReportingDateType(Constants.GROUP_TIME_NAV_BAR, type);
				this.filterTable();
			},
			defaultDateType: BrowserStorageSettings.getReportingDateType(Constants.GROUP_TIME_NAV_BAR),
			defaultFromDate: BrowserStorageSettings.getReportingDateFrom(Constants.GROUP_TIME_NAV_BAR),
			defaultToDate: BrowserStorageSettings.getReportingDateTo(Constants.GROUP_TIME_NAV_BAR)
		});
		$("#workingTimeRequestReportShowInactive").change(() => {
			this.reloadUserList();
		});
		this.userCombobox = new Combobox({
			items: this.config.users,
			formatter: (user) => {
				return user.fullname;
			},
			idElement: $("#workingTimeRequestFilterUserId"),
			filterElement: $("#workingTimeRequestFilterUserFilter"),
			caretElement: $("#workingTimeRequestFilterUserCaret")
		});
		if (globals.isAuthorizedForAllUsers || globals.isGroupLeader) {
			this.config.currentUserId = BrowserStorageSettings.getReportUserFilter(Constants.GROUP_REPORT_FILTER);
			this.userCombobox.setSelectedItemById(this.config.currentUserId);
		}
		$(this.userCombobox).on(Combobox.EVENT_CHANGED, (e, data: User) => {
			this.config.currentUserId = data?.id;
			BrowserStorageSettings.setReportUserFilter(Constants.GROUP_REPORT_FILTER, this.config.currentUserId);
			this.filterTable();
		});
		this.groupDropdown = new GroupDropdown({
			rootElement: $("#workingTimeRequestFilterGroupDropdown"),
			allowSelectionOfNotAssigned: false
		});
		$(this.groupDropdown).on(GroupDropdown.EVENT_SELECTED, (e, group) => {
			this.filterTable();
			this.reloadUserList();
		});
		$("#workingTimeRequestFilterStatus").change(() => {
			this.filterTable();
		});

		// Before the table, which duplicates notice requests with it
		this.initWorkingTimeNoticeDialog();
		this.workingTimeRequestTable = new WorkingTimeRequestTable({
			element: $("#workingTimeRequestTable"),
			initialFilter: this.buildFilterParams(),
			orderable: false,
			clampNote: true,
			allowExpand: true,
			isGroupLeader: this.config.isGroupLeader,
			workingTimeTypes: this.config.workingTimeTypes,
			currentUserId: this.config.currentUserId,
			isGroupLeaderForThemself: this.config.isGroupLeaderForThemself,
			customFieldDefinitions: this.config.customFieldDefinitions,
			workingTimeNoticeDialog: this.workingTimeNoticeDialog
		});
		$(".addAbsenceButton").click(() => {
			this.addAbsence();
		}).toggleClass("disabled", this.getWorkingTimeTypesForAddAbsence().length === 0);

		this.initSubstituteCombobox();
		this.initExport();
	}

	private initSubstituteCombobox() {
		this.substituteCombobox = new Combobox({
			items: this.config.users,
			formatter: (user) => {
				return user.fullname;
			},
			idElement: $("#workingTimeRequestFilterSubstituteId"),
			filterElement: $("#workingTimeRequestFilterSubstituteFilter"),
			caretElement: $("#workingTimeRequestFilterSubstituteCaret")
		});
		$(this.substituteCombobox).on(Combobox.EVENT_CHANGED, () => this.filterTable());
	}

	private initWorkingTimeDialog() {
		this.workingTimeDialog = new WorkingTimeDialog({
			dialogId: "#workingTimeDialog",
			workingTimeTypes: this.config.workingTimeTypes,
			userSelectionEnabled: this.config.users !== undefined,
			users: this.config.users,
			allowedUserIds: this.config.allowedUserIds,
			customFieldDefinitions: this.config.customFieldDefinitions,
			defaultStatusNumber: this.config.defaultStatusNumber,
			isGroupLeaderForThemself: this.config.isGroupLeaderForThemself,
			dateTimeChangeableByUserDayRangeUntil: parseISODate(this.config.workingTimeDateTimeChangeableByUserDayRangeUntil),
			dateTimeChangeableByUserDayRangeMode: this.config.workingTimeDateTimeChangeableByUserDayRangeMode
		});
		$(this.workingTimeDialog).on(WorkingTimeDialog.EVENT_RECORD_CREATED, () => {
			this.workingTimeRequestTable.reload();
		});
		$(this.workingTimeDialog).on(WorkingTimeDialog.EVENT_REQUEST_CREATED, () => {
			this.workingTimeRequestTable.reload();
		});
	}

	private initWorkingTimeNoticeDialog() {
		this.workingTimeNoticeDialog = new WorkingTimeNoticeDialogBridge({
			workingTimeTypes: this.config.workingTimeTypesForNotices,
			showUserRow: this.config.isGroupLeader,
			isGroupLeaderForThemself: this.config.isGroupLeaderForThemself
		});
		$(this.workingTimeNoticeDialog).on(WorkingTimeNoticeDialogBridge.EVENT_NOTICE_SAVED, () => {
			this.workingTimeRequestTable.reload();
		});

		// Only rendered if a working time type requires a notice
		$("#addNoticeButton").click(() => {
			const selectedUser = this.userCombobox.getSelectedItem();
			this.workingTimeNoticeDialog.openForCreate({
				selectedUser: selectedUser ? {id: selectedUser.id, fullname: selectedUser.fullname} : undefined
			});
		});
	}

	private initExport() {
		const exportButton = $("#workingTimeRequestReportExportButton").show();
		$("#workingTimeRequestReportExportButtonLoadingIndicator").hide();

		this.workingTimeRequestTable.config.element.on("processing.dt", (_e, _settings, processing) => {
			if (processing) {
				Button.setLoading(exportButton);
			} else {
				Button.resetLoading(exportButton);
			}
		});

		$("#workingTimeRequestReportExportDropdown").find("a").click((e) => {
			const link = $(e.currentTarget);
			confirmExportOfEmptyTable(this.workingTimeRequestTable.getRowCount(), () => link.data("preview")
				? this.preview()
				: this.export(link.data("exportType") as ExportType));
		});
	}

	private export(exportType: ExportType) {
		void this.requestExport(exportType).then(exportJobId => new DownloadStatus({
			exportJobId,
			exportStatusBaseUrl: "export/status.json",
			exportType: exportType,
			loadingMessage: messages[`downloadStatus.loadingMessage.reports.${exportType}`],
			allowCancellation: true
		}));
	}

	private preview() {
		void this.requestExport(ExportType.Pdf).then(exportJobId => showPdfPreview(
			messages["reports.export.preview"], exportJobId, messages[`downloadStatus.loadingMessage.reports.${ExportType.Pdf}`]));
	}

	/** Starts the export job and resolves with its id; never resolves if no job was started. */
	private requestExport(exportType: ExportType): Promise<string> {
		const params = this.buildFilterParams();
		// Add current sort order to request
		params.push(...this.workingTimeRequestTable.getOrderParams());
		params.push({name: "exportFileType", value: exportType});

		return new Promise(resolve => {
			void api("reports/exportWorkingTimeRequests.json")
				.post(params)
				.done((data: { exportJobId: string }) => {
					if (data.exportJobId) {
						resolve(data.exportJobId);
					}
				});
		});
	}

	private reloadUserList() {
		api("reports/filterWorkingTimeRequestUsers.json")
			.post(this.buildUserListParams())
			.done(data => {
				this.setUsers(data.users);
			});
	}

	private filterTable() {
		this.workingTimeRequestTable.filter(this.buildFilterParams());
	}

	private buildFilterParams(): RequestParam[] {
		return [
			...$("#workingTimeRequestFilterForm").serializeArray(),
			{name: "from", value: getISODate(this.timeNavigationBar.getDateFrom())},
			{name: "to", value: getISODate(this.timeNavigationBar.getDateTo())},
			{name: "activeUsersOnly", value: false},
		];
	}

	private buildUserListParams(): RequestParam[] {
		return [
			{name: "activeUsersOnly", value: !$("#workingTimeRequestReportShowInactive").is(":checked")},
			{name: "groupId", value: this.groupDropdown.getSelectedId()},
		];
	}

	private setUsers(users: User[]) {
		this.userCombobox.updateItems(users);
	}

	protected getWorkingTimeTypesForAddAbsence(): WorkingTimeType[] {
		return this.config.workingTimeTypes.filter((workingTimeType: WorkingTimeType) => {
			return isWorkingTimeTypeAllowedForAddAbsence(workingTimeType, isWorkingTimeDateTimeChangeAllowed());
		});
	}

	private addAbsence(start: Temporal.ZonedDateTime | null = null, end: Temporal.ZonedDateTime | null = null) {
		if (!this.validateSecurityStatus()) {
			return;
		}
		Analytics.trackEvent("Working Time Requests User - Add Vacation", "Working Time");
		const workingTimeTypes = this.getWorkingTimeTypesForAddAbsence();

		this.workingTimeDialog.create({
			workingTimeTypeId: null,
			userId: this.userCombobox.getSelectedItem()?.id,
		}, false, workingTimeTypes);
		this.workingTimeDialog.setStartEndTime(start, end);
	}

	private validateSecurityStatus(): boolean {
		if (!SecurityStatus.isAddWorkingTimeAllowed()) {
			BootstrapModal.info({
				title: "account.expired.workingtime.title",
				message: "account.expired.workingtime",
				escapeText: false
			});
			return false;
		}
		return true;
	}
}
